import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { BankOutlined, ShopOutlined } from "@ant-design/icons";
import {
  formatSubscriptionAmount,
  formatSubscriptionDate,
} from "../subscriptionFormatters";

const { Text } = Typography;
const { TextArea } = Input;

interface CompanySummary {
  id: number;
  name: string;
}

type BillingFrequency = "monthly" | "quarterly" | "semiannual" | "annual";

interface PricingQuote {
  billable_warehouse_count: number;
  warehouse_unit_price: number;
  monthly_unit_price: number;
  gross_amount: number;
  discount_months: number;
  discount_amount: number;
  total_amount: number;
  currency: string;
  rate_source: "catalog" | "negotiated";
}

interface CompanyTermsPayload {
  company: {
    id: number;
    name: string;
    licence_expire_on: string | null;
    next_renewal_date: string;
    billing_frequency: BillingFrequency;
  };
  settings: {
    default_warehouse_monthly_price: number;
    default_credit_limit: number;
    default_grace_days: number;
    default_targeted_days_before_read_only: number;
    default_read_only_days_before_suspension: number;
    default_payment_terms_days: number;
    currency: string;
  };
  policy: {
    collection_mode: "prepaid" | "on_account";
    credit_limit_override: number | null;
    grace_days_override: number | null;
    targeted_days_before_read_only_override: number | null;
    read_only_days_before_suspension_override: number | null;
    payment_terms_days_override: number | null;
  };
  effective_policy: {
    collection_mode: "prepaid" | "on_account";
    credit_limit: number;
    grace_days: number;
    targeted_days_before_read_only: number;
    read_only_days_before_suspension: number;
    payment_terms_days: number;
  };
  pricing: {
    warehouse_unit_price_override: number | null;
    effective_from: string | null;
    monthly_quote: PricingQuote | null;
    annual_quote: PricingQuote | null;
    billing_quote: PricingQuote | null;
    quote_error: { message: string } | null;
  };
  access: {
    access_state: "normal" | "warning" | "read_only" | "suspended";
    reason: string | null;
    outstanding_amount: number;
    overdue_days: number;
    access_enforcement_enabled: boolean;
    restriction_enforced: boolean;
  } | null;
}

interface TermsFormValues {
  collection_mode: "prepaid" | "on_account";
  billing_frequency: BillingFrequency;
  warehouse_unit_price_override?: number;
  credit_limit_override?: number;
  grace_days_override?: number;
  targeted_days_before_read_only_override?: number;
  read_only_days_before_suspension_override?: number;
  payment_terms_days_override?: number;
  revision_reason: string;
}

interface Props {
  open: boolean;
  company: CompanySummary | null;
  onClose: () => void;
  onSuccess: () => void;
}

const accessLabels = {
  normal: { label: "Normal", color: "green" },
  warning: { label: "Avertissement", color: "gold" },
  read_only: { label: "Lecture seule", color: "orange" },
  suspended: { label: "Suspendu", color: "red" },
};

const CompanySubscriptionTermsModal: React.FC<Props> = ({
  open,
  company,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<TermsFormValues>();
  const [data, setData] = useState<CompanyTermsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTerms = useCallback(async () => {
    if (!open || !company) return;
    setLoading(true);
    setData(null);
    try {
      const response = await fetch(
        `/api/subscription/companies/${company.id}/terms`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        },
      );
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Chargement impossible");
      }
      const terms = payload.data as CompanyTermsPayload;
      setData(terms);
      form.setFieldsValue({
        collection_mode: terms.policy.collection_mode,
        billing_frequency: terms.company.billing_frequency,
        warehouse_unit_price_override:
          terms.pricing.warehouse_unit_price_override ?? undefined,
        credit_limit_override:
          terms.policy.credit_limit_override ?? undefined,
        grace_days_override: terms.policy.grace_days_override ?? undefined,
        targeted_days_before_read_only_override:
          terms.policy.targeted_days_before_read_only_override ?? undefined,
        read_only_days_before_suspension_override:
          terms.policy.read_only_days_before_suspension_override ?? undefined,
        payment_terms_days_override:
          terms.policy.payment_terms_days_override ?? undefined,
        revision_reason: "",
      });
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "Chargement impossible",
      );
    } finally {
      setLoading(false);
    }
  }, [company, form, open]);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  const saveTerms = useCallback(
    async (values: TermsFormValues) => {
      if (!company) return;
      setSaving(true);
      try {
        const response = await fetch(
          `/api/subscription/companies/${company.id}/terms`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ...values,
              warehouse_unit_price_override:
                values.warehouse_unit_price_override ?? null,
              credit_limit_override: values.credit_limit_override ?? null,
              grace_days_override: values.grace_days_override ?? null,
              targeted_days_before_read_only_override:
                values.targeted_days_before_read_only_override ?? null,
              read_only_days_before_suspension_override:
                values.read_only_days_before_suspension_override ?? null,
              payment_terms_days_override:
                values.payment_terms_days_override ?? null,
            }),
          },
        );
        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Enregistrement impossible");
        }
        message.success("Conditions commerciales enregistrées");
        if (
          payload.data?.access?.access_enforcement_enabled === false &&
          ["overdue", "credit_limit", "both"].includes(payload.data.access.reason)
        ) {
          message.warning(
            "Les restrictions financières sont calculées, mais leur application globale est désactivée.",
            8,
          );
        }
        onSuccess();
      } catch (error) {
        message.error(
          error instanceof Error ? error.message : "Enregistrement impossible",
        );
      } finally {
        setSaving(false);
      }
    },
    [company, onSuccess],
  );

  const quote = data?.pricing.monthly_quote;
  const annualQuote = data?.pricing.annual_quote;
  const billingQuote = data?.pricing.billing_quote;
  const accessTag = useMemo(
    () =>
      data?.access
        ? accessLabels[data.access.access_state]
        : accessLabels.normal,
    [data],
  );

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Enregistrer"
      cancelText="Annuler"
      confirmLoading={saving}
      width={900}
      title={
        <Space>
          <BankOutlined />
          <span>Conditions commerciales — {company?.name || "Entreprise"}</span>
        </Space>
      }
    >
      <Spin spinning={loading}>
        {data && (
          <>
            <Alert
              showIcon
              type="info"
              message="Effet au prochain renouvellement"
              description={`Le tarif et la périodicité prendront effet au prochain renouvellement, le ${formatSubscriptionDate(
                data.company.next_renewal_date,
              )}. Pour un prépayé, la facture sera émise 7 jours avant cette date et restera impayée jusqu'au règlement. Les factures déjà émises ne seront jamais recalculées.`}
              style={{ marginBottom: 16 }}
            />

            <Descriptions bordered size="small" column={{ xs: 1, md: 3 }}>
              <Descriptions.Item label="Warehouses actifs">
                <Space><ShopOutlined />{quote?.billable_warehouse_count ?? 0}</Space>
              </Descriptions.Item>
              <Descriptions.Item label="Montant par échéance">
                {billingQuote
                  ? formatSubscriptionAmount(
                      billingQuote.total_amount,
                      billingQuote.currency,
                    )
                  : "Non calculable"}
              </Descriptions.Item>
              <Descriptions.Item label="Annuel estimé">
                {annualQuote
                  ? `${formatSubscriptionAmount(
                      annualQuote.total_amount,
                      annualQuote.currency,
                    )} (${annualQuote.discount_months} mois offert)`
                  : "Non calculable"}
              </Descriptions.Item>
              <Descriptions.Item label="État calculé">
                <Tag color={accessTag.color}>{accessTag.label}</Tag>
                {data.access?.access_enforcement_enabled === false &&
                  ["overdue", "credit_limit", "both"].includes(data.access.reason || "") && (
                    <Tag>Observation financière</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Encours">
                {formatSubscriptionAmount(
                  data.access?.outstanding_amount || 0,
                  data.settings.currency,
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Retard maximal">
                {data.access?.overdue_days || 0} jour(s)
              </Descriptions.Item>
            </Descriptions>

            {data.access?.access_enforcement_enabled === false &&
              ["overdue", "credit_limit", "both"].includes(data.access.reason || "") && (
                <Alert
                  type="warning"
                  showIcon
                  message="Restrictions financières non appliquées"
                  description="Les retards et plafonds sont calculés pour cette entreprise, mais le réglage global d’application est désactivé. Les délais configurés s’appliqueront après son activation."
                  style={{ marginTop: 16 }}
                />
              )}

            {data.pricing.quote_error && (
              <Alert
                type="warning"
                showIcon
                message={data.pricing.quote_error.message}
                style={{ marginTop: 16 }}
              />
            )}

            <Divider />
            <Form form={form} layout="vertical" onFinish={saveTerms}>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="collection_mode"
                    label="Mode de règlement"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={[
                        {
                          value: "prepaid",
                          label: "Prépayé — règlement avant accès",
                        },
                        {
                          value: "on_account",
                          label: "À compte — crédit encadré",
                        },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="billing_frequency"
                    label="Périodicité de facturation"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={[
                        { value: "monthly", label: "Mensuelle" },
                        { value: "quarterly", label: "Trimestrielle" },
                        { value: "semiannual", label: "Semestrielle" },
                        { value: "annual", label: "Annuelle" },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="warehouse_unit_price_override"
                    label="Tarif négocié par Warehouse (XOF)"
                    tooltip={`Laisser vide pour appliquer le tarif catalogue de ${formatSubscriptionAmount(
                      data.settings.default_warehouse_monthly_price,
                      data.settings.currency,
                    )}.`}
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      placeholder="Tarif catalogue"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Text strong>Dérogations à la politique globale</Text>
              <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                Laissez un champ vide pour reprendre automatiquement la valeur
                globale. Le plafond exact reste autorisé ; la lecture seule ne
                commence qu'au dépassement.
              </Text>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="credit_limit_override"
                    label="Plafond crédit (XOF)"
                  >
                    <InputNumber
                      min={0}
                      precision={0}
                      placeholder={String(data.settings.default_credit_limit)}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="grace_days_override" label="Tolérance avant restriction (jours)">
                    <InputNumber
                      min={0}
                      precision={0}
                      placeholder={String(data.settings.default_grace_days)}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="targeted_days_before_read_only_override"
                    label="Restrictions ciblées avant lecture seule (jours)"
                  >
                    <InputNumber
                      min={0}
                      precision={0}
                      placeholder={String(data.settings.default_targeted_days_before_read_only)}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="read_only_days_before_suspension_override"
                    label="Avant suspension (jours)"
                  >
                    <InputNumber
                      min={0}
                      precision={0}
                      placeholder={String(
                        data.settings.default_read_only_days_before_suspension,
                      )}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="payment_terms_days_override"
                    label="Échéance facture (jours)"
                  >
                    <InputNumber
                      min={0}
                      precision={0}
                      placeholder={String(data.settings.default_payment_terms_days)}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="revision_reason"
                label="Motif de la décision"
                rules={[
                  { required: true, whitespace: true, message: "Motif obligatoire" },
                  { min: 3, max: 500, message: "Entre 3 et 500 caractères" },
                ]}
              >
                <TextArea rows={3} maxLength={500} showCount />
              </Form.Item>
            </Form>
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default CompanySubscriptionTermsModal;
